'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CatalogueForm, type CatalogueFormRef } from '@/components/partials'
import { type UpdateCatalogueParams } from '@/types/catalogue'
import { useCatalogue, useUpdateCatalogue } from '@/hooks'
import { FormModeEnum } from '@/enums'
import { LoadingIndicator } from '@/components/partials'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const catalogueFormRef = useRef<CatalogueFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)

  const { data: catalogue, isLoading: isFetching } = useCatalogue(id)
  const { mutate: updateCatalogue, isPending: isUpdating } = useUpdateCatalogue()

  const handleSubmit = (data: UpdateCatalogueParams) => {
    updateCatalogue(
      { id, params: data },
      {
        onSuccess: async updatedCatalogue => {
          setIsRedirecting(true)
          queryClient.setQueryData(['catalogue', id], updatedCatalogue)
          await queryClient.invalidateQueries({ queryKey: ['catalogues'] })
          toast.success(t('updated_successfully'))
          router.push(`/galleries/catalogues/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (catalogueFormRef.current) {
        catalogueFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/galleries/catalogues/${id}`)
  }

  if (isFetching) {
    return <LoadingIndicator />
  }

  return (
    <CatalogueForm
      ref={catalogueFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={catalogue}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isUpdating || isRedirecting}
    />
  )
}

export default Update
